import { useTranslate } from "@lobby/ocb-intl";
import { clsx } from "clsx";
import { useContext, useEffect, useRef, useState } from "react";
import type { PropsWithChildren } from "react";
import { Player, PlayerSettingsContext, usePlayerMoneyFormatter } from "../../../entities";
import { Icon, Skeleton, type TPlayerBalancesEx, emitter } from "../../../shared";
import { PlayerBalance } from "../../PlayerBalance";

export function ProfilePlayerBalanceView({ className }: { className?: string }) {
  const [isDummyFetching, setIsDummyFetching] = useState(false);
  const loadingIndicatorRef = useRef<HTMLDivElement>(null);

  const { $t } = useTranslate();

  const playerSettings = useContext(PlayerSettingsContext);

  const { data: player } = Player.usePlayer();
  const { data: balanceData, refetch, isFetching } = Player.useBalances();

  const isPending = isFetching || isDummyFetching;
  const balance = balanceData?.balance ?? ({} as TPlayerBalancesEx["balance"]);

  /*
    Regular balance - only balance field is present
    SS Dual balance - balance and win fields are present
    SS Single balance - all fields are present

    @link https://lobby-v2.stage.c23.games/api-docs/#/Player/Player.getBalancesEx
  */
  const isNonRegularBalance = Object.keys(balance).length > 1;

  function toggleBalanceVisibility() {
    emitter.emit("PLAYER_BALANCE_VISIBILITY_CHANGED", !playerSettings.isBalanceVisible);
  }

  const refetchBalances = async () => {
    if (isPending) return;

    setIsDummyFetching(true);
    await refetch();
    setTimeout(() => setIsDummyFetching(false), 1000);
  };

  useEffect(() => {
    if (isPending && loadingIndicatorRef.current) {
      loadingIndicatorRef.current.querySelector('svg')!.classList.add("animate-spin");
    }
  }, [isPending]);

  return (
    <div className={clsx("profile-area", className)}>
      <div className="flex-c-sb gap-5">
        <div className="truncate font-medium text-18 lg:text-16">
          {String(player?.displayableName)}
        </div>
        <div
          ref={loadingIndicatorRef}
          className="cursor-pointer"
          onClick={refetchBalances}
          onAnimationIteration={(e) =>
              !isPending && e.currentTarget.querySelector('svg')!.classList.remove("animate-spin")
          }
        >
          <Icon className="text-18" name="reload" />
        </div>
      </div>

      <div className="mt-5 flex-c-sb gap-3">
        <div className="flex items-center gap-1 font-medium uppercase">
          <span
            style={{
              color: "var(--text-secondary-color)",
            }}
          >
            {$t({ defaultMessage: "Balance" })}
          </span>
          <button className="p-1 text-20" type="button" onClick={toggleBalanceVisibility}>
            {playerSettings.isBalanceVisible ? (
              <Icon name="openedEye" />
            ) : (
              <Icon name="closedEye" />
            )}
          </button>
        </div>
        <BalanceValue className="!h-7 !w-32" loading={playerSettings.isBalanceVisible && isPending}>
          <PlayerBalance className="font-medium lg:text-18" />
        </BalanceValue>
      </div>

      {isNonRegularBalance && (
        <>
          <hr
            style={{
              borderColor: "var(--text-secondary-color)",
            }}
            className="my-2"
          />
          <div className="space-y-2 text-12 lg:text-14">
            {"credit" in balance && (
              <BalanceRow
                title={$t({ defaultMessage: "Credit" })}
                value={balance.credit as number}
                loading={isPending}
              />
            )}
            {"bonus" in balance && (
              <BalanceRow
                title={$t({ defaultMessage: "Bonus" })}
                value={balance.bonus as number}
                loading={isPending}
              />
            )}
            {"win" in balance && (
              <BalanceRow
                title={$t({ defaultMessage: "Total win" })}
                value={balance.win as number}
                loading={isPending}
              />
            )}
            {"redeem" in balance && (
              <BalanceRow
                title={$t({ defaultMessage: "Redeemable" })}
                value={balance.redeem as number}
                loading={isPending}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface IBalanceRowProps {
  title: string;
  value: number;
  loading: boolean;
}

function BalanceRow({ title, value, loading }: IBalanceRowProps) {
  const formatMoney = usePlayerMoneyFormatter();

  return (
    <div className="flex-c-sb gap-2">
      <span
        style={{
          color: "var(--text-secondary-color)",
        }}
        className="whitespace-nowrap"
      >
        {title}
      </span>
      <span
        style={{
          borderColor: "var(--text-secondary-color)",
        }}
        className="-mb-2 min-w-10 grow border-b-2 border-dotted"
      />
      <BalanceValue loading={loading}>{formatMoney(value)}</BalanceValue>
    </div>
  );
}

function BalanceValue({
  className,
  loading,
  children,
}: PropsWithChildren<{ className?: string; loading: boolean }>) {
  return loading ? (
    <Skeleton className={clsx("h-4 w-20 rounded-sm lg:h-5", className)} />
  ) : (
    <span className="truncate">{children}</span>
  );
}
